/**
 * Finalized / cancelled leagues never change, so the list poll tells the server which ones this
 * client already holds (as one fingerprint) and gets `{ id, unchanged: true }` stubs back for them.
 * Must stay byte-identical to `leagueFinalMarker` / `finalLeaguesFingerprint` in
 * `backend/db/leagues/leagueFeed.js`.
 */

import { LEAGUE_REACTION_ACTIVITY_CAP } from "./leagueReactions.ts";
import type { League } from "./types.ts";

export interface LeagueListUnchangedStub {
    id: string;
    unchanged: true;
}

export function isUnchangedLeagueStub(row: { id: string; unchanged?: unknown }): row is LeagueListUnchangedStub {
    return (row as LeagueListUnchangedStub).unchanged === true;
}

export function leagueFinalMarker(league: Pick<League, "id" | "settledAt" | "cancelledAt">): string | null {
    if (league.cancelledAt) return `${league.id}:c:${league.cancelledAt}`;
    if (league.settledAt) return `${league.id}:s:${league.settledAt}`;
    return null;
}

/**
 * When the list poll refreshes `members.coins` after a settlement but this merge keeps
 * stale open plays from a prior detail hydrate, available chips become
 * `newCoins − oldOpenStake` (often 0) while the server still has the real free balance.
 * Keep bankroll fields paired with any held open plays until a full detail refetch.
 */
function mergeListSlimMembers(held: League, incomingMembers: League["members"]): League["members"] {
    const openUserIds = new Set(
        (held.plays || [])
            .filter((play) => play.status === "open")
            .map((play) => play.userId),
    );
    if (openUserIds.size === 0) return incomingMembers;
    const heldByUser = new Map(held.members.map((row) => [row.userId, row]));
    return incomingMembers.map((row) => {
        if (!openUserIds.has(row.userId)) return row;
        const prev = heldByUser.get(row.userId);
        if (!prev) return row;
        return {
            ...row,
            coins: prev.coins,
            startingCoins: prev.startingCoins,
        };
    });
}

/**
 * Hub feed is card-only (`listSlim`, empty plays/settlements + `hubCard`). Keep richer
 * detail/cache feeds when the list omits them; take hubCard from the list, and keep
 * member bankroll fields paired with any held open plays.
 */
export function mergeListSlimLeague(held: League | undefined, incoming: League): League {
    const listSlim = Boolean(incoming.listSlim);
    const { listSlim: _drop, ...rest } = incoming;
    if (!listSlim || !held) return rest;
    return {
        ...rest,
        hubCard: rest.hubCard ?? held.hubCard,
        members: mergeListSlimMembers(held, rest.members),
        // List is card-only — never replace a detail hydrate with empty/partial feeds.
        plays: held.plays,
        settlements: held.settlements,
        seatTopUps: held.seatTopUps ?? rest.seatTopUps,
        reactionEvents: held.reactionEvents ?? rest.reactionEvents,
        bullionBattle: rest.bullionBattle ?? held.bullionBattle,
    };
}

/**
 * Full-detail poll: take the server snapshot, but keep optimistic emoji rows the live
 * throw path already prepended that this response has not caught up to yet.
 */
export function mergePolledLeagueDetail(held: League | undefined, incoming: League): League {
    if (!held) return incoming;
    const incomingEvents = incoming.reactionEvents ?? [];
    const seen = new Set(incomingEvents.map((row) => row.id));
    const extra = (held.reactionEvents ?? []).filter((row) => row.id && !seen.has(row.id));
    if (extra.length === 0) return incoming;
    const merged = [...incomingEvents, ...extra].sort((a, b) => {
        const delta = new Date(b.at).getTime() - new Date(a.at).getTime();
        return delta !== 0 ? delta : String(b.id).localeCompare(String(a.id));
    });
    // Keep the same window as `LEAGUE_REACTION_ACTIVITY_CAP`.
    return { ...incoming, reactionEvents: merged.slice(0, LEAGUE_REACTION_ACTIVITY_CAP) };
}

function fnv1a32(str: string, seed: number): string {
    let h = seed >>> 0;
    for (let i = 0; i < str.length; i += 1) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(16).padStart(8, "0");
}

export function finalLeaguesFingerprint(markers: string[]): string {
    const joined = [...markers].sort().join("|");
    return `${markers.length}-${fnv1a32(joined, 0x811c9dc5)}${fnv1a32(joined, 0x2166136b)}`;
}
